import React, { useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { ACCEPT, humanSize } from "@/lib/upload";
import { MediaPreview, MediaThumb } from "@/components/common/MediaPreview";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Paperclip, Download, Trash2 } from "lucide-react";

// Attach images / audio / video / PDFs to any record (note, hearing, ...). Files are documents with attachedTo.
export function AttachmentPanel({ matterId, attachedTo, compact = false, readOnly = false }) {
  const { db, attachFile, remove, fileUrl } = useApp();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [open, setOpen] = useState(null);
  const files = db.documents.filter((d) => d.attachedTo?.collection === attachedTo.collection && d.attachedTo?.id === attachedTo.id);

  const onPick = async (list) => {
    for (const file of Array.from(list || [])) {
      setProgress(0);
      try { const rec = await attachFile({ file, matterId, attachedTo, onProgress: setProgress }); toast.success(`${rec.name} attached.`); }
      catch (e) { toast.error(e?.response?.data?.detail || e.message || "Upload failed."); }
    }
    setProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className={compact ? "mt-2" : "mt-3"} data-testid={`attachments-${attachedTo.collection}-${attachedTo.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-2"><Paperclip className="mr-1 inline h-3 w-3" />{files.length ? `${files.length} attachment${files.length > 1 ? "s" : ""}` : "Attachments"}</span>
        {!readOnly && (
          <>
            <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => onPick(e.target.files)} data-testid={`attach-input-${attachedTo.id}`} />
            <button onClick={() => inputRef.current?.click()} disabled={progress !== null} className="text-xs text-copper hover:underline disabled:opacity-50" data-testid={`attach-btn-${attachedTo.id}`}>{progress !== null ? `Uploading… ${progress}%` : "+ Attach file"}</button>
          </>
        )}
      </div>
      {progress !== null && <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-secondary"><div className="h-full bg-copper transition-[width]" style={{ width: `${progress}%` }} /></div>}
      {files.length > 0 && (
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {files.map((f) => (
            <div key={f.id} className="group relative rounded-lg border border-line bg-card p-1.5" data-testid={`attachment-${f.id}`}>
              <button className="w-full text-left" onClick={() => setOpen(f)}><MediaThumb doc={f} className="h-20" /></button>
              <div className="mt-1 truncate text-[11px] text-ink" title={f.name}>{f.name}</div>
              <div className="flex items-center justify-between text-[10px] text-ink-muted"><span>{f.size || humanSize(f.sizeBytes)}</span>
                <span className="flex gap-1.5">
                  <a href={`${fileUrl(f.id)}&download=1`} title="Download" data-testid={`attachment-download-${f.id}`}><Download className="h-3 w-3 hover:text-copper" /></a>
                  {!readOnly && <button onClick={() => remove("documents", f.id, f.name)} title="Move to Trash" data-testid={`attachment-delete-${f.id}`}><Trash2 className="h-3 w-3 hover:text-danger" /></button>}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {open && (<>
            <DialogHeader className="px-4 pt-4"><DialogTitle className="font-display text-xl truncate">{open.name}</DialogTitle></DialogHeader>
            <div className="h-[70vh] bg-ink/90"><MediaPreview doc={open} /></div>
            <div className="flex items-center justify-between px-4 py-3 text-xs text-ink-2"><span>{open.type} · {open.size}</span>
              <Button size="sm" variant="outline" asChild><a href={`${fileUrl(open.id)}&download=1`} data-testid="attachment-dialog-download"><Download className="mr-1 h-3.5 w-3.5" />Download</a></Button></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
